// src/utils/viewportHelper.jsx

// Liefert ein kontinuierliches Fenster [west, east] (east > west), unwrap bei Antimeridian.
export const getNormFromMap = (map) => {
  if (!map) return null
  const b = map.getBounds()
  let west = b.getWest()
  let east = b.getEast()
  if (east <= west) east += 360
  const north = b.getNorth()
  const south = b.getSouth()
  return { west, east, north, south }
}

// Verschiebt eine Longitude mit ±360° in das aktuelle Fenster (Legacy/optional).
export const adjustLonToNorm = (lon, norm) => {
  if (!norm) return lon
  let x = lon
  x = x + 360 * Math.round((norm.west - x) / 360)
  while (x < norm.west) x += 360
  while (x > norm.east) x -= 360
  return x
}

// Test, ob eine Longitude im Fenster liegt.
export const inWindow = (lon, norm) => !norm || (lon >= norm.west && lon <= norm.east)

// Bringe Longitude in die Kopie, deren Zentrum der Viewport-Mitte am nächsten liegt.
// Dadurch erscheinen Features wieder, wenn du weit pannst (mehrere Weltkopien).
export const lonToWindow = (lon, norm) => {
  if (!norm) return lon
  const center = (norm.west + norm.east) / 2
  return lon + 360 * Math.round((center - lon) / 360)
}

// Punkte ins Fenster projizieren.
// - renderOnlyInViewport=true: nur Punkte im Fenster rendern.
// - includeIndex=true: fügt properties.__idx für z. B. Right-Click-Delete hinzu.
export function projectPointsToViewport(geojson, norm, renderOnlyInViewport = true, includeIndex = false) {
  if (!geojson || !geojson.features) return geojson
  const features = geojson.features
    .map((f, i) => {
      if (!f?.geometry?.coordinates) return null
      const [lon, lat] = f.geometry.coordinates
      const x = lonToWindow(lon, norm)

      if (renderOnlyInViewport && norm) {
        const inside = x >= norm.west && x <= norm.east
        if (!inside) return null
      }

      const nf = {
        ...f,
        geometry: { ...f.geometry, coordinates: [x, lat] },
      }
      if (includeIndex) nf.properties = { ...(f.properties || {}), __idx: i }
      return nf
    })
    .filter(Boolean)
  return { ...geojson, features }
}

// Web-Mercator latitude ↔ projected-y helpers. A straight screen line between
// two lon/lat points (how Leaflet draws a 2-point leg, and where rhumbline
// vessel icons sit) is straight in Mercator-y, NOT linear in latitude. Clipping
// must therefore interpolate the cut latitude in Mercator-y; doing it linearly
// in lat shifts the clipped vertex off the drawn line (≈2 km on long legs),
// which the vessel icon then visibly misses at high zoom.
const latToMercY = (lat) =>
  Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2))
const mercYToLat = (y) => (2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180 / Math.PI

// 2-Punkt-Segment an [west, east] entlang der X-Achse clippen
// (Lat in Mercator-y mitführen, damit der Clip auf der gezeichneten Linie bleibt).
const clipSegmentToWindow = (lon1, lat1, lon2, lat2, west, east) => {
  const dx = lon2 - lon1
  const dy = lat2 - lat1

  // Vertikal
  if (dx === 0) {
    if (lon1 < west || lon1 > east) return null
    return [
      [lon1, lat1],
      [lon2, lat2],
    ]
  }

  // Liang–Barsky (nur X-Grenzen)
  let t0 = 0
  let t1 = 1
  const update = (p, q) => {
    if (p === 0) {
      if (q < 0) return false
      return true
    }
    const r = q / p
    if (p < 0) {
      if (r > t1) return false
      if (r > t0) t0 = r
    } else if (p > 0) {
      if (r < t0) return false
      if (r < t1) t1 = r
    }
    return true
  }

  if (!update(-dx, lon1 - west)) return null
  if (!update(dx, east - lon1)) return null
  if (t0 > t1) return null

  // Interpolate the clipped latitude in Mercator-y (straight drawn line), not
  // linearly in lat — otherwise the clipped endpoint drifts off the rendered
  // rhumbline and the vessel icon sits beside it.
  const my1 = latToMercY(lat1)
  const my2 = latToMercY(lat2)
  const xa = lon1 + t0 * dx
  const ya = mercYToLat(my1 + t0 * (my2 - my1))
  const xb = lon1 + t1 * dx
  const yb = mercYToLat(my1 + t1 * (my2 - my1))
  return [
    [xa, ya],
    [xb, yb],
  ]
}

// Linien ins Fenster projizieren.
// - renderOnlyInViewport=true: nur sichtbare Segmente zeichnen; bei Bedarf clippen.
// - clipEdges=true: Segmente an [west, east] kappen.
// - renderOnlyInViewport=false: Legacy (sanftes Wrap beider Enden).
export function projectLinesToViewport(geojson, norm, renderOnlyInViewport = true, clipEdges = false) {
  if (!norm) return geojson
  if (!geojson || !geojson.features) return geojson
  const features = geojson.features
    .map((f) => {
      const coords = f?.geometry?.coordinates
      if (!coords || coords.length < 2) return null

      // Normalize first vertex into the current viewport window, then
      // unwrap each subsequent vertex to stay within 180° of the previous.
      // Works for any-length LineString (2-point rhumbline or multi-vertex greatcircle).
      const adjusted = new Array(coords.length)
      adjusted[0] = [lonToWindow(coords[0][0], norm), coords[0][1]]
      for (let i = 1; i < coords.length; i++) {
        const prevX = adjusted[i - 1][0]
        const lon = coords[i][0]
        const x = lon + 360 * Math.round((prevX - lon) / 360)
        adjusted[i] = [x, coords[i][1]]
      }

      const anyInside = adjusted.some(([x]) => x >= norm.west && x <= norm.east)
      const spansWindow =
        Math.min(...adjusted.map(([x]) => x)) < norm.west &&
        Math.max(...adjusted.map(([x]) => x)) > norm.east
      if (renderOnlyInViewport && !anyInside && !spansWindow && !clipEdges)
        return null

      let nf = {
        ...f,
        geometry: {
          ...f.geometry,
          coordinates: adjusted,
        },
      }

      // clipEdges path only supports 2-point legs (original behavior).
      if (
        renderOnlyInViewport &&
        clipEdges &&
        adjusted.length === 2 &&
        !(adjusted[0][0] >= norm.west && adjusted[0][0] <= norm.east &&
          adjusted[1][0] >= norm.west && adjusted[1][0] <= norm.east)
      ) {
        const clipped = clipSegmentToWindow(
          adjusted[0][0], adjusted[0][1],
          adjusted[1][0], adjusted[1][1],
          norm.west, norm.east,
        )
        if (!clipped) return null
        nf.geometry.coordinates = clipped
      }

      // analyse.wp1/wp2 stay the store's waypoints: line vertices are turf-rounded.
      return nf
    })
    .filter(Boolean)
  return { ...geojson, features }
}

